
import { switchMap, mergeMap, takeUntil, throttleTime, filter, map, catchError, tap } from 'rxjs/operators'
import * as Raven from 'raven-js'
import { Observable, empty, from, zip, of } from 'rxjs'
import { combineEpics } from 'redux-observable'
import * as identity from 'lodash/identity'
import { get, post } from 'helpers/observableAjaxHelpers'
import { DELETE_MEMBER_ACCOUNT, GET_MEMBER_INFO, SET_MEMBER_INFO, setMemberInfoActionCreator } from 'actions/memberActions'
import { RawMember, Member } from 'interfaces/member'
import { FORM_ONBOARDING_PROPERTY, FORM_SETTINGS_CREDIT, FORM_SETTINGS_CREDIT_EXTENDED, FORM_MEMBER_PROFILE, FORM_SOCIAL_PROFILE } from 'constants/forms'
import { DELETE_MEMBER_ACCOUNT_URL, GET_MEMBER_INFO_URL, PATCH_MEMBER_INFO_URL, POST_SOCIAL_PROFILE_URL } from 'interfaces/ajax'
import { Epic } from 'interfaces/middleware'
import { LOGOUT, logOutActionCreator } from 'actions/authActions'
import { setCarsActionCreator, setHomesActionCreator } from 'actions/propertyActions'
import { normalize } from 'helpers/normalizeHelpers'
import { SUBMIT_FORM, formSubmittedSuccessfullyActionCreator, formSubmitFailedActionCreator } from 'actions/formActions'
import { AjaxError } from 'rxjs/ajax'
import { VALIDATION_ERROR_STATUS_CODE } from 'constants/ajax'
import { FormErrorsResponse } from 'interfaces/component'
import { SocialProfileFormValues, RawFeedUser, FeedUser } from 'interfaces/feed'
import { FORM_TRANSFORMATIONS, TransformationFunction } from 'epics/formEpic'
import { compose, MiddlewareAPI } from 'redux'
import { FormNameType } from 'interfaces/form'
import { AnyDict } from 'interfaces'
import { State } from 'interfaces/state'
import { stopSubmit } from 'redux-form'
import { CompositeAction } from 'interfaces/action'
import { memberFeedInfoSelector } from 'selectors/feedSelectors'
import { userImageSelector } from 'selectors/userImagesSelectors'
import { byType } from 'rxOperators/byType'
import { end } from 'rxOperators/end'
import { mapSafe, mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { formSubmitted } from 'rxOperators/formsLegacy'
import { consoleRelay } from 'components/features/webSocketLogger/ConsoleWSRelay'

const onboardingUpdateMemberEpic: Epic = (action$) =>
  action$
    .pipe(formSubmitted(
      FORM_ONBOARDING_PROPERTY,
      FORM_SETTINGS_CREDIT, FORM_SETTINGS_CREDIT_EXTENDED,
    )).pipe(
    // FORM_LOGIN, FORM_DEVICE_VERIFICATION and FORM_ONBOARDING_REGISTER are covered in authEpic
      mergeMap(({ payload }) => {
        const actions = []

        if (payload.formName === FORM_ONBOARDING_PROPERTY) {
          const rawMember: RawMember | null = payload.response
          if (!rawMember) {
            return empty()
          }

          const { cars, homes, ...member }: RawMember = rawMember
          actions.push(
            setCarsActionCreator(normalize(cars, 'id')),
            setHomesActionCreator(normalize(homes, 'id')),
            setMemberInfoActionCreator(member),
          )
        } else if (
          payload.formName === FORM_SETTINGS_CREDIT
        || payload.formName === FORM_SETTINGS_CREDIT_EXTENDED
        ) {
          const { member } = payload.response
          actions.push(setMemberInfoActionCreator(member))
        }

        return from(actions)
      }))


const getMembersInfoEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_MEMBER_INFO)).pipe(
    // TODO: @pmyagkov ideally this should be rewritten using discard triggers
    // but now I'm afraid to break something
      takeUntil(action$.pipe(byType(LOGOUT))),
      throttleTime(100),
      mergeMap(() =>
        get(GET_MEMBER_INFO_URL).pipe(
          tap(({ response }) => {
            if (response.extended_logging) {
              consoleRelay.start()
            }
          }),
          mapSafe(({ response }) => setMemberInfoActionCreator(response))
        )
      ))

const setExternalUserContextsEpic: Epic = (action$) =>
  action$
    .pipe(byType(SET_MEMBER_INFO))
    .pipe(end(({ payload }) => {
      const { email, analytics_user_id } = payload
      if (email) {
        Raven.setUserContext({ email })
      }

      if (analytics_user_id) {
        ga('set', 'userId', analytics_user_id)
      }
    }))

const deleteMemberAccountEpic: Epic = (action$) =>
  action$
    .pipe(byType(DELETE_MEMBER_ACCOUNT)).pipe(
      mergeMap(() =>
        post(DELETE_MEMBER_ACCOUNT_URL)
          .pipe(mapSafe(() => logOutActionCreator())) // tslint:disable-line no-unnecessary-callback-wrapper
      ))

type SubmitResult = { status: number, response?: any, errors?: FormErrorsResponse }
//                                               any ???

type SubmitPartialFormFunction =
  & ((endpoint: typeof POST_SOCIAL_PROFILE_URL, apiPayload: SocialProfileFormValues) => Observable<SubmitResult>)
  & ((endpoint: typeof PATCH_MEMBER_INFO_URL, apiPayload: Partial<Member>) => Observable<SubmitResult>)

const submitPartialForm: SubmitPartialFormFunction = (endpoint: any, apiPayload: any) => {
  return post(endpoint, { body: apiPayload }).pipe(
    map(({ status, response }): SubmitResult => ({ status, response })),
    catchError((error: AjaxError): Observable<SubmitResult> => {
      const { status, xhr: { response }} = error

      if (status === VALIDATION_ERROR_STATUS_CODE) {
        const responseErrors: FormErrorsResponse = (typeof response === 'string' ? JSON.parse(response) : response) || {}
        return of({
          status,
          errors: responseErrors,
        })
      }

      return of({
        status,
        errors: {
          _error: [`Unexpected error: ${ status || 'Script error' }`],
        },
      })
    }))
}

type WrapperFunction<TFormValues = AnyDict, TRawFormValues = AnyDict> = (values: TFormValues | null) => TRawFormValues | null
const getTransformFunction = (form: FormNameType, store: MiddlewareAPI<State>): WrapperFunction => {
  const transformFunction = (FORM_TRANSFORMATIONS[form] || identity) as TransformationFunction

  const wrapperFunction: WrapperFunction = (values) => {
    if (values === null) {
      return null
    }

    return transformFunction(values, store.getState())
  }

  return wrapperFunction
}

const makeFakeRawMember = (memberFeed: FeedUser, state: State): RawFeedUser => {
  const { avatarId, ...restProps } = memberFeed
  const image = userImageSelector(state, avatarId)
  return {
    ...restProps,
    avatar: image && image.image ? image.image : null,
  }
}

function createPostSocialProfile$ (username: string, avatar: number, state: State) {
  const memberFeed = memberFeedInfoSelector(state)

  const prevUsername = memberFeed && memberFeed.username || null
  const nextUsername = username || null

  const prevAvatar = memberFeed && memberFeed.avatarId || null
  const nextAvatar = avatar || null
  const hasChanges = prevUsername !== nextUsername || prevAvatar !== nextAvatar
  if (hasChanges) {
    return submitPartialForm(POST_SOCIAL_PROFILE_URL, { username, avatar })
  }

  const fakeResponse = memberFeed ? makeFakeRawMember(memberFeed, state) : {}
  return of({ status: 200, response: fakeResponse })
}

const profileFormSubmitEpic: Epic = (action$, store) => {
  const transformProfileValues = compose(
    getTransformFunction(FORM_MEMBER_PROFILE, store),
    getTransformFunction(FORM_SOCIAL_PROFILE, store),
  )

  return action$
    .pipe(byType(SUBMIT_FORM)).pipe(
      filter((action) => action.payload.formName === FORM_MEMBER_PROFILE))
    .pipe(mergeMapSafe((action) => {
      const { formName, values: preValues, extra } = action.payload

      const values = transformProfileValues(preValues)
      if (!values) {
        return empty()
      }

      const {
        avatar,
        username,
        ...restProfile
      } = values

      const state = store.getState()
      const postSocialProfile$ = createPostSocialProfile$(username, avatar, state)
      const postMemberProfile$ = submitPartialForm(PATCH_MEMBER_INFO_URL, restProfile)

      return zip(postSocialProfile$, postMemberProfile$).pipe(
        switchMap(([feedMemberResponse, memberResponse]) => {
          if (feedMemberResponse.response && memberResponse.response) {
            const rawFeedMember: RawFeedUser = feedMemberResponse.response

            const { avatar: avatarImage, ...restFeedMember } = rawFeedMember

            const formSubmittedAction = formSubmittedSuccessfullyActionCreator(
              formName,
              {
                feedMember: {
                  ...restFeedMember,
                  ...avatarImage ? { avatarId: avatarImage.id } : { avatarId: null },
                },
                avatar: avatarImage,
                member: memberResponse.response,
              },
              values,
              extra,
            )

            return of<CompositeAction>(
              formSubmittedAction,
              stopSubmit(FORM_MEMBER_PROFILE)
            )
          }

          const errors: FormErrorsResponse = {
            ...(feedMemberResponse as any).errors,
            ...memberResponse.errors,
          }

          return of<CompositeAction>(
            formSubmitFailedActionCreator(formName),
            stopSubmit(formName, errors)
          )
        }
        ))
    }))
}

export default combineEpics(
  onboardingUpdateMemberEpic,
  setExternalUserContextsEpic,
  getMembersInfoEpic,
  deleteMemberAccountEpic,
  profileFormSubmitEpic
)
